import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { oldMobileValuationAPI, localPurchaseAPI, saleMediaAPI } from '../services/api';

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

const QC = [
  ['display', 'Display'],
  ['touch', 'Touch'],
  ['rearCamera', 'Rear camera'],
  ['frontCamera', 'Front camera'],
  ['speaker', 'Speaker'],
  ['microphone', 'Microphone'],
  ['wifi', 'Wi-Fi'],
  ['bluetooth', 'Bluetooth'],
  ['network', 'SIM / network'],
  ['charging', 'Charging'],
  ['fingerprint', 'Fingerprint'],
  ['faceUnlock', 'Face unlock'],
  ['buttons', 'Buttons']
];

const REPAIRS = [
  ['Front camera blur/dust', 500],
  ['Back camera issue', 900],
  ['Battery weak', 1200],
  ['Charging port', 700],
  ['Speaker/mic issue', 500],
  ['Display duplicate/replaced risk', 1800],
  ['Display line/spot', 2500],
  ['Body dent/back glass', 800],
  ['No bill/box risk buffer', 500],
  ['Software/lock risk', 1500]
];

function initialQc() {
  const o = {};
  QC.forEach(([k]) => {
    o[k] = true;
  });
  return o;
}

function gradeOf({ qc, battery, screen, scratch, box, bill, charger, notes }) {
  let score = 0;
  const reasons = [];
  const fails = QC.filter(([k]) => !qc[k]).length;
  if (!fails) reasons.push('Sab functional checks ON');
  else if (fails === 1) {
    score += 2;
    reasons.push('1 QC issue');
  } else if (fails === 2) {
    score += 4;
    reasons.push('2 QC issues');
  } else {
    score += 6;
    reasons.push(`${fails} QC issues`);
  }
  const bat = battery === '' ? null : Number(battery);
  if (bat != null && !Number.isNaN(bat)) {
    if (bat < 65) {
      score += 4;
      reasons.push('Battery below 65%');
    } else if (bat < 78) {
      score += 2;
      reasons.push('Battery below 78%');
    } else if (bat < 88) {
      score += 1;
      reasons.push('Battery below 88%');
    }
  }
  const txt = `${screen || ''} ${scratch || ''} ${notes || ''}`.toLowerCase();
  if (/icloud|frp|lock|water|dead|motherboard|emmc|cpu|stolen/.test(txt)) {
    score += 6;
    reasons.push('High risk note');
  } else if (/crack|broken|line|spot|blur|dust|dusty|replace|duplicate|yellow|dent|heavy/.test(txt)) {
    score += 3;
    reasons.push('Display/camera/body issue');
  }
  const docs = [box, bill, charger].filter(Boolean).length;
  if (docs === 0) {
    score += 2;
    reasons.push('No box/bill/charger');
  } else if (docs === 1) {
    score += 1;
    reasons.push('Few accessories');
  }
  return {
    grade: score <= 1 ? 'A+' : score <= 3 ? 'A' : score <= 6 ? 'B' : 'C',
    reasons,
    score,
    fails
  };
}

function minus(v, d) {
  d = Math.max(0, Number(d || 0));
  return v
    ? {
        suggestedMinINR: Math.max(0, Math.round(Number(v.suggestedMinINR || 0) - d)),
        suggestedMaxINR: Math.max(0, Math.round(Number(v.suggestedMaxINR || 0) - d)),
        recommendedINR: Math.max(0, Math.round(Number(v.recommendedINR || 0) - d))
      }
    : null;
}

export default function OldPhoneValuation() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [ramGB, setRamGB] = useState('');
  const [storageGB, setStorageGB] = useState('');
  const [ageMonths, setAgeMonths] = useState('');
  const [purchaseYear, setPurchaseYear] = useState('');
  const [battery, setBattery] = useState('');
  const [screen, setScreen] = useState('');
  const [scratch, setScratch] = useState('');
  const [box, setBox] = useState(false);
  const [bill, setBill] = useState(false);
  const [charger, setCharger] = useState(false);
  const [qc, setQc] = useState(initialQc);
  const [notes, setNotes] = useState('');
  const [manual, setManual] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [valuation, setValuation] = useState(null);
  const [repairName, setRepairName] = useState('');
  const [repairCost, setRepairCost] = useState('');
  const [repairs, setRepairs] = useState([]);
  const [agreed, setAgreed] = useState('');

  const [sellerName, setSellerName] = useState('');
  const [sellerPhone, setSellerPhone] = useState('');
  const [sellerAddress, setSellerAddress] = useState('');
  const [imei1, setImei1] = useState('');
  const [imei2, setImei2] = useState('');
  const [intakeLoading, setIntakeLoading] = useState(false);
  const [intakeDone, setIntakeDone] = useState(null);
  const [sellerPhoto, setSellerPhoto] = useState(null);

  const auto = useMemo(
    () => gradeOf({ qc, battery, screen, scratch, box, bill, charger, notes }),
    [qc, battery, screen, scratch, box, bill, charger, notes]
  );
  const grade = manual || auto.grade;
  const totalRepair = repairs.reduce((s, r) => s + Number(r.cost || 0), 0);
  const adjusted = minus(valuation, totalRepair);

  const payload = {
    brand: brand.trim(),
    model: model.trim(),
    ramGB: ramGB === '' ? undefined : Number(ramGB),
    storageGB: storageGB === '' ? undefined : Number(storageGB),
    ageMonths: ageMonths === '' ? undefined : Number(ageMonths),
    purchaseYear: purchaseYear === '' ? undefined : Number(purchaseYear),
    batteryHealth: battery === '' ? undefined : Number(battery),
    conditionGrade: grade,
    conditionGradeSource: manual ? 'manual_override' : 'auto_checklist',
    conditionGradeReasons: auto.reasons,
    screenCondition: screen.trim(),
    scratchesLevel: scratch.trim(),
    hasBox: box,
    hasBill: bill,
    hasCharger: charger,
    qcReport: { ...qc },
    managerNotes: notes.trim()
  };

  async function run() {
    setError('');
    setLoading(true);
    setValuation(null);
    setRepairs([]);
    setIntakeDone(null);
    try {
      const res = await oldMobileValuationAPI.suggest(payload);
      if (!res.data?.success) throw new Error(res.data?.message || 'Failed');
      setValuation(res.data.valuation);
      setStep(4);
    } catch (e) {
      const isTimeout = e.code === 'ECONNABORTED' || /timeout/i.test(String(e.message || ''));
      setError(
        isTimeout
          ? 'AI slow hai. Backend fallback enabled hai; backend restart ke baad dobara try karein.'
          : e.response?.data?.message || e.message || 'Request failed'
      );
    } finally {
      setLoading(false);
    }
  }

  function addRepair(n, c) {
    n = String(n || '').trim();
    c = Math.max(0, Number(c || 0));
    if (!n || !c) return;
    setRepairs((rs) => [...rs, { name: n, cost: c }]);
    setRepairName('');
    setRepairCost('');
  }

  function buildValuationSnapshot() {
    return {
      valuation,
      repairs,
      adjusted,
      grade,
      autoReasons: auto.reasons,
      payload
    };
  }

  function report() {
    const failedQc = QC.filter(([k]) => !qc[k]).map(([, l]) => l);
    const betterIf = [];
    if (failedQc.length) betterIf.push(`QC sab pass ho to risk kam: abhi OFF — ${failedQc.join(', ')}`);
    if (!box || !bill || !charger) {
      betterIf.push('Box + bill + charger complete hone par trust/resale behtar');
    }
    const bat = Number(battery);
    if (!Number.isNaN(bat) && bat < 88) {
      betterIf.push('Battery health 88%+ se battery-risk buffer kam ho sakta hai');
    }
    const tp =
      valuation?.talkingPoints && valuation.talkingPoints.length
        ? valuation.talkingPoints
        : (valuation?.factors || []).slice(0, 8);
    const lines = [
      'A2zTak - Old Phone Valuation Report',
      `Date: ${new Date().toLocaleString('en-IN')}`,
      `Device: ${brand} ${model}`,
      `Grade: ${grade}`,
      '',
      'Original valuation:',
      `Recommended: ${fmt(valuation?.recommendedINR)}`,
      '',
      'Re-valued:',
      `Recommended buy: ${fmt(adjusted?.recommendedINR)}`,
      agreed ? `Agreed: ${fmt(agreed)}` : '',
      '',
      ...(tp.length ? tp.map((x) => `- ${x}`) : []),
      '',
      ...(betterIf.length ? betterIf.map((x) => `- ${x}`) : [])
    ];
    return lines.filter((x) => x !== '' && x != null).join('\n');
  }

  function download() {
    const blob = new Blob([report()], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `old-phone-valuation-${brand || 'phone'}-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function submitLocalPurchase() {
    setError('');
    const agreedNum = Number(agreed);
    if (!Number.isFinite(agreedNum) || agreedNum <= 0) {
      setError('Pehle counter par final agreed price fill karein.');
      return;
    }
    if (!sellerName.trim() || sellerPhone.replace(/\D/g, '').length < 10) {
      setError('Seller name aur 10-digit phone zaroori hai.');
      return;
    }
    if (imei1.replace(/\D/g, '').length < 14) {
      setError('Valid IMEI 1 (15 digits) zaroori hai.');
      return;
    }
    setIntakeLoading(true);
    try {
      const res = await localPurchaseAPI.create({
        ...payload,
        agreedPrice: agreedNum,
        sellerName: sellerName.trim(),
        sellerPhone: sellerPhone.trim(),
        sellerAddress: sellerAddress.trim(),
        imei1: imei1.replace(/\D/g, ''),
        imei2: imei2.replace(/\D/g, ''),
        valuationSnapshot: buildValuationSnapshot()
      });
      if (!res.data?.success) throw new Error(res.data?.message || 'Failed');

      const intake = res.data;
      if (sellerPhoto && intake.sellerIntake?.customerId && intake.productId != null) {
        try {
          const fd = new FormData();
          fd.append('salePhoto', sellerPhoto);
          fd.append('kind', 'seller_intake');
          fd.append('customerId', intake.sellerIntake.customerId);
          fd.append('productId', intake.productId);
          fd.append('imeiIndex', String(intake.imeiIndex ?? 0));
          await saleMediaAPI.upload(fd);
        } catch (photoErr) {
          console.warn('Seller photo upload:', photoErr.response?.data?.message || photoErr.message);
        }
      }

      setIntakeDone(intake);
      setSellerPhoto(null);
      setStep(5);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Stock add failed');
    } finally {
      setIntakeLoading(false);
    }
  }

  const steps = ['1. Device', '2. Body / battery', '3. Functional QC', '4. Notes & run AI'];
  if (valuation) steps.push('5. Price & report');
  if (intakeDone) steps.push('6. Stock added');

  return (
    <div>
      <div className="form-card mb-3">
        <h4 className="fw-700 mb-1">Purana phone - QC & AI valuation</h4>
        <p className="text-muted small mb-0">
          AI price range + repair minus. Agreed price ke baad seedha branch stock mein add karein (sale price baad mein).
        </p>
      </div>

      <div className="d-flex gap-2 flex-wrap mb-3">
        {steps.map((x, i) => (
          <button
            key={x}
            type="button"
            className={`btn btn-sm ${step === i ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => i <= 4 || valuation ? setStep(i) : null}
            disabled={i === 5 && !intakeDone}
          >
            {x}
          </button>
        ))}
      </div>

      {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}

      {step === 0 && (
        <div className="form-card">
          <h5>Device</h5>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Brand *</label>
              <input className="form-control" value={brand} onChange={(e) => setBrand(e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label">Model *</label>
              <input className="form-control" value={model} onChange={(e) => setModel(e.target.value)} />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label">RAM GB</label>
              <input type="number" className="form-control" value={ramGB} onChange={(e) => setRamGB(e.target.value)} />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label">Storage GB</label>
              <input type="number" className="form-control" value={storageGB} onChange={(e) => setStorageGB(e.target.value)} />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label">Age months</label>
              <input type="number" className="form-control" value={ageMonths} onChange={(e) => setAgeMonths(e.target.value)} />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label">Purchase year</label>
              <input type="number" className="form-control" value={purchaseYear} onChange={(e) => setPurchaseYear(e.target.value)} />
            </div>
          </div>
          <button className="btn btn-primary mt-3" disabled={!brand.trim() || !model.trim()} onClick={() => setStep(1)}>
            Next
          </button>
        </div>
      )}

      {step === 1 && (
        <div className="form-card">
          <h5>Body, battery, accessories</h5>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Battery health %</label>
              <input type="number" className="form-control" value={battery} onChange={(e) => setBattery(e.target.value)} />
            </div>
            <div className="col-md-8">
              <label className="form-label">Scratches/body</label>
              <input className="form-control" value={scratch} onChange={(e) => setScratch(e.target.value)} />
            </div>
            <div className="col-12">
              <label className="form-label">Screen / camera issue</label>
              <input className="form-control" value={screen} onChange={(e) => setScreen(e.target.value)} />
            </div>
            <div className="col-12 d-flex flex-wrap gap-3">
              <label><input type="checkbox" checked={box} onChange={(e) => setBox(e.target.checked)} /> Box</label>
              <label><input type="checkbox" checked={bill} onChange={(e) => setBill(e.target.checked)} /> Bill</label>
              <label><input type="checkbox" checked={charger} onChange={(e) => setCharger(e.target.checked)} /> Charger</label>
            </div>
          </div>
          <div className="mt-3">
            <button className="btn btn-outline-secondary me-2" onClick={() => setStep(0)}>Back</button>
            <button className="btn btn-primary" onClick={() => setStep(2)}>Next</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="form-card">
          <h5>Functional QC</h5>
          <p className="text-muted small">ON = working, OFF = issue</p>
          <div className="row g-2">
            {QC.map(([k, l]) => (
              <div className="col-md-6" key={k}>
                <div className="d-flex justify-content-between align-items-center border rounded px-3 py-2">
                  <span>{l}</span>
                  <input type="checkbox" checked={!!qc[k]} onChange={(e) => setQc((q) => ({ ...q, [k]: e.target.checked }))} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 border rounded bg-light p-2 small">
            Auto grade: <strong>{auto.grade}</strong> — {auto.reasons.join(', ')}
          </div>
          <div className="mt-3">
            <button className="btn btn-outline-secondary me-2" onClick={() => setStep(1)}>Back</button>
            <button className="btn btn-primary" onClick={() => setStep(3)}>Next</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="form-card">
          <h5>Manager notes & AI</h5>
          <div className="border rounded p-3 mb-3">
            <div className="d-flex justify-content-between">
              <div>
                <small className="text-muted">Grade</small>
                <div className="fs-4 fw-700">
                  {grade} <span className="badge bg-success">{manual ? 'manual' : 'auto'}</span>
                </div>
              </div>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowManual((o) => !o)}>
                Manual override
              </button>
            </div>
            {showManual && (
              <select className="form-select form-select-sm my-2" value={manual} onChange={(e) => setManual(e.target.value)}>
                <option value="">Auto</option>
                <option>A+</option>
                <option>A</option>
                <option>B</option>
                <option>C</option>
              </select>
            )}
          </div>
          <label className="form-label">Extra notes</label>
          <textarea className="form-control mb-3" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
          <button className="btn btn-outline-secondary me-2" onClick={() => setStep(2)}>Back</button>
          <button className="btn btn-success" disabled={loading || !brand.trim() || !model.trim()} onClick={run}>
            {loading ? 'AI checking...' : 'AI se valuation lo'}
          </button>
        </div>
      )}

      {step === 4 && valuation && adjusted && (
        <div className="form-card">
          <h5 className="text-success">Price range</h5>
          <div className="row g-2 mb-3">
            <div className="col-6 text-muted">Safest buy</div>
            <div className="col-6 text-end fw-700">{fmt(adjusted.suggestedMinINR)}</div>
            <div className="col-6 text-muted">Recommended</div>
            <div className="col-6 text-end fw-700 text-brand fs-5">{fmt(adjusted.recommendedINR)}</div>
            <div className="col-6 text-muted">Last limit</div>
            <div className="col-6 text-end fw-700 text-danger">{fmt(adjusted.suggestedMaxINR)}</div>
          </div>

          <h6>Repair cost minus</h6>
          <div className="d-flex flex-wrap gap-2 mb-2">
            {REPAIRS.map(([n, c]) => (
              <button key={n} type="button" className="btn btn-sm btn-outline-secondary" onClick={() => addRepair(n, c)}>
                {n} - {fmt(c)}
              </button>
            ))}
          </div>

          <label className="form-label">Counter par final agreed price *</label>
          <input
            type="number"
            className="form-control mb-3"
            value={agreed}
            onChange={(e) => setAgreed(e.target.value)}
            placeholder={String(adjusted.recommendedINR || '')}
          />

          <hr />
          <h6 className="fw-700">Seller & IMEI — branch stock mein add karein</h6>
          <p className="text-muted small">
            Sale / offer price baad mein Products page se set hoga. Abhi sirf buying record + QC save hoga.
          </p>
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label">Seller name *</label>
              <input className="form-control" value={sellerName} onChange={(e) => setSellerName(e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label">Seller mobile *</label>
              <input className="form-control" value={sellerPhone} onChange={(e) => setSellerPhone(e.target.value)} placeholder="10 digits" />
            </div>
            <div className="col-12">
              <label className="form-label">Address (optional)</label>
              <input className="form-control" value={sellerAddress} onChange={(e) => setSellerAddress(e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label">IMEI 1 *</label>
              <input className="form-control" value={imei1} onChange={(e) => setImei1(e.target.value)} placeholder="15 digits" />
            </div>
            <div className="col-md-6">
              <label className="form-label">IMEI 2 (optional)</label>
              <input className="form-control" value={imei2} onChange={(e) => setImei2(e.target.value)} />
            </div>
            <div className="col-12">
              <label className="form-label">Seller + phone photo (optional)</label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="form-control"
                onChange={(e) => setSellerPhoto(e.target.files?.[0] || null)}
              />
              <div className="form-text">Counter par seller/phone ki photo — customer profile mein save hogi.</div>
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2">
            <button className="btn btn-success" disabled={intakeLoading} onClick={submitLocalPurchase}>
              {intakeLoading ? 'Adding stock…' : 'Add to branch stock'}
            </button>
            <button className="btn btn-outline-success" onClick={download}>Download report</button>
            <button className="btn btn-outline-secondary" onClick={() => navigator.clipboard.writeText(report()).catch(() => {})}>
              Copy report
            </button>
          </div>
        </div>
      )}

      {step === 5 && intakeDone && (
        <div className="form-card">
          <div className="alert alert-success">
            <h5 className="mb-2">Stock add ho gaya</h5>
            <p className="mb-1"><strong>{intakeDone.productName}</strong></p>
            <p className="mb-1">Buy price: {fmt(agreed)} · Status: <span className="badge bg-warning text-dark">Pricing pending</span></p>
            {intakeDone.sellerIntake?.awarded && (
              <p className="mb-0 small">
                Seller ko +100 reward points · WhatsApp template{' '}
                {intakeDone.sellerIntake?.whatsappSent ? 'bheja gaya ✓' : 'pending — number check karein'}
              </p>
            )}
          </div>
          <p className="text-muted small">
            Ab Products page par jaake <strong>Set sale price</strong> karein — product pic URL aur real photos wahan add ho sakte hain.
          </p>
          <div className="d-flex gap-2">
            <button className="btn btn-primary" onClick={() => navigate(`/products?highlight=${intakeDone.productId}`)}>
              Products → finalize sale
            </button>
            <button className="btn btn-outline-secondary" onClick={() => { setStep(0); setIntakeDone(null); setValuation(null); }}>
              Naya phone
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
